import React, { useEffect, useState } from 'react';

import AccountMenu from '@/components/AccountMenu';
import { ILayoutProps } from '@/components/Layout';
import { Logo } from '@/components/shared/Logo';
import { EAccountDisplayState } from '@/components/AccountPanel/AccountPanel';
import { Chip } from '@/components/shared/Chip/Chip';
import { useSubscription } from '@/providers/SubscriptionProvider';
import { team } from '@/assets/icons';
import { BottomNavigation } from '@material-ui/core';
import { useLocation, useNavigate } from 'react-router';

import AsaphBottomNavigationButton, {
	type AsaphBottomNavigationButtonProps,
} from './AsaphBottomNavigationButton';
import styles from './AsaphNavigationPanel.module.scss';
import {
	AsaphNavigationButtonProps,
	AsaphTopNavigationButton,
} from './AsaphTopNavigationButton';

export interface AsaphNavigationPanelProps {
	position: 'top' | 'bottom';
	buttons: AsaphNavigationButtonProps[];
	setAccountOpen: ILayoutProps['setAccountOpen'];
}

export const AsaphNavigationPanel = ({
	position,
	buttons,
	setAccountOpen,
}: AsaphNavigationPanelProps) => {
	const location = useLocation();
	const navigate = useNavigate();
	const { status: subscriptionStatus, setPaidModalOpen } = useSubscription();
	const [value, setValue] = useState(
		getCurrentlySelectedTabIndex(location.pathname, buttons),
	);
	useEffect(() => {
		setValue(getCurrentlySelectedTabIndex(location.pathname, buttons));
	}, [location.pathname, buttons]);

	function onChange(_: object, newValue: number) {
		if (buttons[newValue]?.url) navigate(buttons[newValue].url);
	}

	function renderNavigation(position: AsaphNavigationPanelProps['position']) {
		switch (position) {
			case 'top':
				return (
					<div className={styles['top-nav-panel']}>
						<div
							className={styles['logo-wrapper']}
							onClick={() => navigate(buttons[0]?.url)}
						>
							<Logo />
						</div>
						{buttons
							.filter((b) => !b.hidden)
							.map(({ onClick, ...buttonProps }, i) => (
								<AsaphTopNavigationButton
									onClick={() => {
										navigate(buttonProps.url);
										onClick?.();
									}}
									{...buttonProps}
									key={buttonProps.label}
									isSelected={
										buttonProps.url === location.pathname
									}
								/>
							))}
						<AsaphTopNavigationButton
							label="Team"
							url=""
							key="team"
							defaultIcon={team}
							selectedIcon={team}
							onClick={() => setAccountOpen({ open: true, defaultDisplayState: EAccountDisplayState.TEAM })}
						/>
						<AsaphTopNavigationButton
							label={subscriptionStatus ? 'Plan' : 'Upgrade plan'}
							url=""
							key="plan"
							className={styles['settings-link']}
							onClick={() => setPaidModalOpen(true)}
							trailingComponent={
								<Chip color="light-gray" variant="rounded">
									{subscriptionStatus ? 'Leader' : 'Free'}
								</Chip>
							}
						/>
						<AsaphTopNavigationButton
							label="Settings"
							url=""
							key="settings"
							onClick={() => setAccountOpen({ open: true })}
						/>
						<AccountMenu
							className={styles['account-menu']}
							setAccountOpen={setAccountOpen}
						/>
					</div>
				);
			case 'bottom':
			default:
				return (
					<BottomNavigation
						value={value}
						onChange={(object, newValue) => {
							onChange(object, newValue);
						}}
						showLabels
						classes={{ root: styles['bottom-nav-panel'] }}
					>
						{buttons.map((buttonProps) => {
							return (
								<AsaphBottomNavigationButton
									{...(buttonProps as AsaphBottomNavigationButtonProps)}
									key={buttonProps.label}
									isSelected={
										buttonProps.url === location.pathname
									}
								/>
							);
						})}
					</BottomNavigation>
				);
		}
	}

	return renderNavigation(position);
};

function getCurrentlySelectedTabIndex(
	currentUrl: string,
	buttons: AsaphNavigationButtonProps[],
) {
	return buttons.reduce(
		(acc, button, i) => (button.url === currentUrl ? i : acc),
		-1,
	);
}

export default AsaphNavigationPanel;
